import { useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { ShieldCheck, KeyRound, Smartphone, Car, HeartPulse, Loader2, CheckCircle2, HelpCircle } from 'lucide-react';
import Layout from '../components/Layout';
import { useAuth } from '../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STEP = { CODE: 0, FORM: 1, DONE: 2 };

export default function ActivateTagPage() {
  const [sp] = useSearchParams();
  const { token } = useAuth();
  const [codeToken, setCodeToken] = useState('');
  const [step, setStep] = useState(STEP.CODE);
  const [tagId, setTagId] = useState((sp.get('tag') || '').toUpperCase());
  const [code, setCode] = useState((sp.get('code') || '').toUpperCase());
  const [mobile, setMobile] = useState('');
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const [form, setForm] = useState({
    vehicle_type: 'car', registration_number: '', make: '', model: '', colour: '',
    owner_name: '', primary_mobile: '', alternate_mobile: '', blood_group: '',
    emergency_contact_name: '', emergency_contact_number: '', emergency_contact_relationship: '',
    allergies: '', medical_conditions: '', important_medical_info: '', emergency_notes: '',
    public_show_blood_group: true, public_show_medical: true, public_show_emergency_contact: true,
    vehicle_photo_base64: '',
  });

  const onPhoto = (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        const c = document.createElement('canvas');
        const maxW = 800; const ratio = Math.min(1, maxW / img.width);
        c.width = img.width * ratio; c.height = img.height * ratio;
        c.getContext('2d').drawImage(img, 0, 0, c.width, c.height);
        setForm((s) => ({ ...s, vehicle_photo_base64: c.toDataURL('image/jpeg', 0.75) }));
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(f);
  };

  const verifyCode = async () => {
    setErr(''); setBusy(true);
    try {
      const { data } = await axios.post(`${API}/qr/activate/verify-code`, { tag_id: tagId.trim(), activation_code: code.trim() });
      setCodeToken(data.code_token);
      setForm((f) => ({ ...f, primary_mobile: mobile }));
      setStep(STEP.FORM);
    } catch (e) { setErr(e.response?.data?.detail || 'Verification failed'); } finally { setBusy(false); }
  };

  const submit = async (e) => {
    e.preventDefault(); setErr(''); setBusy(true);
    try {
      const { data } = await axios.post(`${API}/qr/activate/submit`, { tag_id: tagId.trim(), code_token: codeToken, ...form }, token ? { headers: { Authorization: `Bearer ${token}` } } : {});
      setResult(data); setStep(STEP.DONE);
    } catch (ex) { setErr(ex.response?.data?.detail || 'Activation failed'); } finally { setBusy(false); }
  };

  return (
    <Layout>
      <section className="max-w-xl mx-auto px-4 py-12" data-testid="activate-page">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 bg-navy/10 text-navy px-3 py-1 rounded-full text-xs font-semibold mb-3"><ShieldCheck size={12} /> Emergency Tag Activation</div>
          <h1 className="h-display text-3xl md:text-4xl">Activate your <em className="font-serif-display italic font-normal text-navy">emergency tag</em></h1>
          <p className="mt-2 text-sm text-neutral-600">Takes about 2 minutes · Secured with your activation code</p>
        </div>

        <Steps step={step} />

        {step === STEP.CODE && (
          <div className="card-soft p-6 space-y-4" data-testid="step-code">
            <Field icon={KeyRound} label="Tag ID (from your sticker)">
              <input value={tagId} onChange={(e) => setTagId(e.target.value.toUpperCase())} placeholder="TAG0001" className="w-full px-3 py-3 rounded-lg border border-black/15 focus:outline-none focus:ring-2 focus:ring-navy/30 font-mono tracking-wider" data-testid="input-tag-id" />
            </Field>
            <Field icon={ShieldCheck} label="Activation Code">
              <input value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="XXXXXX" maxLength={8} className="w-full px-3 py-3 rounded-lg border border-black/15 focus:outline-none focus:ring-2 focus:ring-navy/30 font-mono tracking-widest" data-testid="input-code" />
            </Field>
            <Field icon={Smartphone} label="Your mobile number">
              <input value={mobile} onChange={(e) => setMobile(e.target.value)} placeholder="+91 98xxxxxxxx" className="w-full px-3 py-3 rounded-lg border border-black/15 focus:outline-none focus:ring-2 focus:ring-navy/30" data-testid="input-mobile" />
            </Field>
            {err && <p className="text-sm text-red-600" data-testid="error">{err}</p>}
            <button disabled={busy || !tagId || !code || !mobile} onClick={verifyCode} className="btn-primary w-full justify-center disabled:opacity-60" data-testid="verify-code-btn">
              {busy ? <><Loader2 size={16} className="animate-spin" /> Verifying…</> : 'Continue'}
            </button>
            <Link to="/activate/help" className="text-xs text-navy hover:underline flex items-center gap-1 justify-center" data-testid="need-help-link"><HelpCircle size={12} /> Need Help? Don't have the activation code?</Link>
          </div>
        )}

        {step === STEP.FORM && (
          <form onSubmit={submit} className="space-y-4" data-testid="step-form">
            <Section title="Vehicle Details" icon={Car}>
              <Row2>
                <Sel label="Vehicle type*" val={form.vehicle_type} onChange={(v) => setForm({ ...form, vehicle_type: v })} opts={[['car', 'Car'], ['bike', 'Bike'], ['other', 'Other']]} test="fld-vehicle-type" />
                <Inp label="Reg number*" val={form.registration_number} onChange={(v) => setForm({ ...form, registration_number: v.toUpperCase() })} placeholder="GJ06XX1234" test="fld-reg" />
                <Inp label="Make*" val={form.make} onChange={(v) => setForm({ ...form, make: v })} placeholder="Maruti" test="fld-make" />
                <Inp label="Model*" val={form.model} onChange={(v) => setForm({ ...form, model: v })} placeholder="Swift" test="fld-model" />
                <Inp label="Colour*" val={form.colour} onChange={(v) => setForm({ ...form, colour: v })} placeholder="White" test="fld-colour" />
              </Row2>
              <label className="block text-xs mt-3"><span className="text-neutral-500 uppercase tracking-wide">Vehicle photo (optional)</span>
                <input type="file" accept="image/*" onChange={onPhoto} className="mt-1 block w-full text-sm" data-testid="fld-vehicle-photo" />
                {form.vehicle_photo_base64 && <img src={form.vehicle_photo_base64} alt="preview" className="mt-2 rounded-lg max-h-40" data-testid="vehicle-photo-preview" />}
              </label>
            </Section>

            <Section title="Owner Details" icon={ShieldCheck}>
              <Row2>
                <Inp label="Owner name*" val={form.owner_name} onChange={(v) => setForm({ ...form, owner_name: v })} test="fld-owner" />
                <Inp label="Primary mobile*" val={form.primary_mobile} onChange={(v) => setForm({ ...form, primary_mobile: v })} test="fld-primary-mobile" />
                <Inp label="Alternate mobile" val={form.alternate_mobile} onChange={(v) => setForm({ ...form, alternate_mobile: v })} test="fld-alt-mobile" />
                <Sel label="Blood group" val={form.blood_group} onChange={(v) => setForm({ ...form, blood_group: v })} opts={[['', '—'], ['A+', 'A+'], ['A-', 'A-'], ['B+', 'B+'], ['B-', 'B-'], ['AB+', 'AB+'], ['AB-', 'AB-'], ['O+', 'O+'], ['O-', 'O-']]} test="fld-blood" />
              </Row2>
            </Section>

            <Section title="Emergency Contact" icon={HeartPulse}>
              <Row2>
                <Inp label="Name*" val={form.emergency_contact_name} onChange={(v) => setForm({ ...form, emergency_contact_name: v })} test="fld-ec-name" />
                <Inp label="Mobile*" val={form.emergency_contact_number} onChange={(v) => setForm({ ...form, emergency_contact_number: v })} test="fld-ec-mobile" />
                <Inp label="Relationship" val={form.emergency_contact_relationship} onChange={(v) => setForm({ ...form, emergency_contact_relationship: v })} placeholder="e.g. Spouse" test="fld-ec-rel" />
              </Row2>
            </Section>

            <Section title="Medical / Emergency Info (optional)" icon={HeartPulse}>
              <div className="space-y-3">
                <Area label="Allergies" val={form.allergies} onChange={(v) => setForm({ ...form, allergies: v })} test="fld-allergies" />
                <Area label="Medical conditions" val={form.medical_conditions} onChange={(v) => setForm({ ...form, medical_conditions: v })} test="fld-conditions" />
                <Area label="Important medical info" val={form.important_medical_info} onChange={(v) => setForm({ ...form, important_medical_info: v })} test="fld-med" />
                <Area label="Emergency notes" val={form.emergency_notes} onChange={(v) => setForm({ ...form, emergency_notes: v })} test="fld-notes" />
              </div>
              <div className="mt-4 space-y-1 text-xs">
                <Chk label="Show blood group on public emergency page" val={form.public_show_blood_group} onChange={(v) => setForm({ ...form, public_show_blood_group: v })} />
                <Chk label="Show medical info on public emergency page" val={form.public_show_medical} onChange={(v) => setForm({ ...form, public_show_medical: v })} />
                <Chk label="Show emergency contact on public emergency page" val={form.public_show_emergency_contact} onChange={(v) => setForm({ ...form, public_show_emergency_contact: v })} />
              </div>
            </Section>

            {err && <p className="text-sm text-red-600" data-testid="error">{err}</p>}
            <button type="submit" disabled={busy} className="btn-primary w-full justify-center disabled:opacity-60" data-testid="submit-activation-btn">
              {busy ? 'Activating…' : 'Confirm & Activate'}
            </button>
          </form>
        )}

        {step === STEP.DONE && result && (
          <div className="card-soft p-8 text-center space-y-3" data-testid="step-done">
            <div className="w-16 h-16 rounded-full bg-green-100 text-green-600 flex items-center justify-center mx-auto"><CheckCircle2 size={30} /></div>
            <h3 className="h-display text-2xl">Your Emergency Tag is now active</h3>
            <div className="text-sm text-neutral-600 space-y-1">
              <p><span className="font-semibold">Tag ID:</span> {result.tag_id}</p>
              <p><span className="font-semibold">Activated:</span> {new Date(result.activated_at).toLocaleString()}</p>
            </div>
            <p className="text-xs text-neutral-500 mt-4">Place this QR Emergency Tag visibly on your vehicle so that someone can contact or notify you during an emergency.</p>
            <div className="flex gap-2 justify-center pt-3">
              <Link to="/dashboard" className="btn-primary" data-testid="go-dashboard">Go to my tags</Link>
              <Link to={`/tag/${result.tag_id.replace('TAG', '')}`} className="btn-outline" data-testid="view-public">View public profile</Link>
            </div>
          </div>
        )}
      </section>
    </Layout>
  );
}

function Steps({ step }) {
  const items = ['Code', 'Details', 'Done'];
  return (
    <div className="flex items-center justify-between mb-6 text-xs">
      {items.map((label, i) => (
        <div key={label} className="flex items-center gap-2 flex-1">
          <span className={`w-6 h-6 rounded-full flex items-center justify-center font-bold ${i <= step ? 'bg-navy text-white' : 'bg-black/10 text-neutral-500'}`}>{i + 1}</span>
          <span className={i <= step ? 'font-semibold' : 'text-neutral-500'}>{label}</span>
          {i < items.length - 1 && <span className="flex-1 h-px bg-black/10" />}
        </div>
      ))}
    </div>
  );
}

const Field = ({ icon: Icon, label, children }) => (<label className="block"><span className="text-[11px] text-neutral-500 uppercase tracking-wide flex items-center gap-1"><Icon size={11} /> {label}</span><div className="mt-1">{children}</div></label>);
const Row2 = ({ children }) => <div className="grid sm:grid-cols-2 gap-3">{children}</div>;
const Inp = ({ label, val, onChange, placeholder, test }) => (<label className="block text-xs"><span className="text-neutral-500 uppercase tracking-wide">{label}</span><input value={val} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className="mt-1 w-full px-3 py-2 rounded-lg border border-black/15 text-sm focus:outline-none focus:ring-2 focus:ring-navy/30" data-testid={test} /></label>);
const Area = ({ label, val, onChange, test }) => (<label className="block text-xs"><span className="text-neutral-500 uppercase tracking-wide">{label}</span><textarea value={val} onChange={(e) => onChange(e.target.value)} rows={2} className="mt-1 w-full px-3 py-2 rounded-lg border border-black/15 text-sm focus:outline-none focus:ring-2 focus:ring-navy/30 resize-none" data-testid={test} /></label>);
const Sel = ({ label, val, onChange, opts, test }) => (<label className="block text-xs"><span className="text-neutral-500 uppercase tracking-wide">{label}</span><select value={val} onChange={(e) => onChange(e.target.value)} className="mt-1 w-full px-3 py-2 rounded-lg border border-black/15 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-navy/30" data-testid={test}>{opts.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select></label>);
const Chk = ({ label, val, onChange }) => (<label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={val} onChange={(e) => onChange(e.target.checked)} /> {label}</label>);
const Section = ({ title, icon: Icon, children }) => (<div className="card-soft p-5"><div className="flex items-center gap-2 mb-3 pb-3 border-b border-black/5"><Icon size={14} className="text-navy" /><h3 className="font-semibold text-sm">{title}</h3></div>{children}</div>);
